import * as React from 'react';
import { Card, CardActionArea, CardContent } from 'ui';
import { CardContentDetails } from 'consumer-page-widgets/CardContentDetails';
import { CardImage } from './CardImage';
import { CardTitle } from './CardTitle';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { consumer } from '@tock/coretypes';
import { find } from 'es-toolkit/compat';
import Anchor from 'components/Anchor';
import Skeleton from '@material-ui/lab/Skeleton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardRoot: {
      borderRadius: 3,
      overflow: 'visible',
    },
    contentRoot: {
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: theme.spacing(1.5),
    },
    cardActionAreaRoot: {
      padding: 0,
      '&:hover': {
        backgroundColor: theme.colors.bgDefault,
        textDecoration: 'none !important',
      },
      '& h3': {
        transition: `${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut}`,
      },
      '&:hover h3': {
        color: theme.colors.fgPrimaryBase,
        '@media (hover: none)': {
          color: theme.colors.fgDefault,
        },
      },
      '&:focus h3': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:focus .MuiCardActionArea-focusHighlight': {
        boxShadow: theme.boxShadows.focus,
      },
      '&:active h3': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:hover .MuiCardActionArea-focusHighlight': {
        opacity: 0,
      },
    },
  })
);

const useStylesSkeletonCard = makeStyles((theme: Theme) =>
  createStyles({
    skeletonCard: {
      height: 0,
      width: '100%',
      paddingBottom: theme.mediaRatios.threeTwo,
      borderRadius: 3,
    },
    background: {
      backgroundColor: theme.colors.bgLight,
    },
  })
);

export interface Props {
  altText: string;
  body?: string;
  className?: string;
  dominantColor: string;
  highlightedNote?: JSX.Element | string;
  href: string;
  image: string;
  showBody?: boolean;
  subtitle?: React.ReactNode;
  subtitleDetailed?: React.ReactNode;
  title: string;
  widgetModifiers?: consumer.WidgetModifier[];
}

export const PhotoCard: React.FunctionComponent<Props> = ({
  altText,
  body,
  className,
  dominantColor,
  highlightedNote,
  href,
  image,
  showBody,
  subtitle,
  subtitleDetailed,
  title,
  widgetModifiers,
  ...rest
}) => {
  const classes = useStyles();

  const showDescription = !!find(widgetModifiers, { key: 'showDescription' });
  const showReleasesInfo = !!find(widgetModifiers, { key: 'showReleasesInfo' });
  const subTitleDisplayType = find(widgetModifiers, { key: 'subtitleDisplayType' });
  return (
    <Card className={className} classes={{ root: classes.cardRoot }} elevation={0} {...rest}>
      <CardActionArea
        classes={{
          root: classes.cardActionAreaRoot,
        }}
        component={Anchor}
        href={href}
        role="link"
        disableRipple
      >
        <CardImage image={image} altText={altText} dominantColor={dominantColor} rounded />
        <CardContent classes={{ root: classes.contentRoot }}>
          <CardTitle
            gutterBottom={showDescription || showBody || showReleasesInfo}
            title={title}
            subtitle={
              subTitleDisplayType && subTitleDisplayType.value === 'neighborhood'
                ? subtitleDetailed
                : subtitle
            }
          />
          <CardContentDetails
            body={body}
            highlightedNote={highlightedNote}
            showBody={showDescription || showBody}
            showReleasesInfo={showReleasesInfo}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PhotoCardSkeleton = (props: any) => {
  const classes = useStylesSkeletonCard();
  return (
    <div {...props}>
      <Skeleton
        className={`${classes.skeletonCard} ${classes.background}`}
        variant="rect"
        animation="wave"
      />
      <Skeleton className={classes.background} variant="text" animation="wave" />
      <Skeleton className={classes.background} variant="text" width="60%" animation="wave" />
      <Skeleton className={classes.background} variant="text" width="40%" animation="wave" />
    </div>
  );
};
